/**
 * Inverse Kinematics for Universal Robots
 *
 * Uses numerical Damped Least Squares method for robust IK solving
 */

import { inverseKinematicsNumerical, inverseKinematicsMultipleSolutions } from './URInverseKinematicsNumerical.js';

/**
 * Compute inverse kinematics for UR robot
 * @param {Object} targetPose - Target end-effector pose
 * @param {Object} targetPose.position - Target position {x, y, z} in meters
 * @param {Object} targetPose.rotation - Target rotation {rx, ry, rz} in radians (axis-angle)
 * @param {Object} dh - DH parameters for the robot
 * @param {Array<number>} initialJoints - Initial joint guess (optional)
 * @returns {Array<Array<number>>} Array of possible joint angle solutions
 */
export function inverseKinematics(targetPose, dh, initialJoints = null) {
  // Use numerical solver with multiple initial guesses to find different solutions
  return inverseKinematicsMultipleSolutions(targetPose, dh, initialJoints);
}

/**
 * Pick the best IK solution based on proximity to current joint angles
 * @param {Array<Array<number>>} solutions - Array of IK solutions
 * @param {Array<number>} currentJoints - Current joint angles [q1, q2, q3, q4, q5, q6]
 * @returns {Array<number>} Best joint solution, or null if no solutions
 */
export function pickBestSolution(solutions, currentJoints) {
  if (solutions.length === 0) {
    return null;
  }

  if (solutions.length === 1) {
    return normalizeJointAngles(solutions[0]);
  }

  // Find solution with minimum distance to current joints
  let bestSolution = solutions[0];
  let minDistance = jointDistance(solutions[0], currentJoints);

  for (let i = 1; i < solutions.length; i++) {
    const dist = jointDistance(solutions[i], currentJoints);
    if (dist < minDistance) {
      minDistance = dist;
      bestSolution = solutions[i];
    }
  }

  return normalizeJointAngles(bestSolution);
}

/**
 * Calculate the distance between two joint configurations
 * Considers the circular nature of joint angles
 * @param {Array<number>} joints1 - First joint configuration
 * @param {Array<number>} joints2 - Second joint configuration
 * @returns {number} Distance metric
 */
function jointDistance(joints1, joints2) {
  let distance = 0;

  for (let i = 0; i < joints1.length; i++) {
    // Normalize both angles to [-π, π]
    const a1 = normalizeAngle(joints1[i]);
    const a2 = normalizeAngle(joints2[i]);

    // Calculate shortest angular distance
    let diff = Math.abs(a1 - a2);
    if (diff > Math.PI) {
      diff = 2 * Math.PI - diff;
    }

    distance += diff * diff;
  }

  return Math.sqrt(distance);
}

/**
 * Normalize angle to [-π, π] range
 * @param {number} angle - Angle in radians
 * @returns {number} Normalized angle
 */
function normalizeAngle(angle) {
  let normalized = angle % (2 * Math.PI);
  if (normalized > Math.PI) {
    normalized -= 2 * Math.PI;
  } else if (normalized < -Math.PI) {
    normalized += 2 * Math.PI;
  }
  return normalized;
}

/**
 * Normalize all joint angles in a solution to [-π, π]
 * @param {Array<number>} joints - Joint angles
 * @returns {Array<number>} Normalized joint angles
 */
function normalizeJointAngles(joints) {
  return joints.map(normalizeAngle);
}

/**
 * Check if a joint configuration is within limits
 * @param {Array<number>} joints - Joint angles
 * @param {Array<Object>} limits - Joint limits [{min, max}, ...]
 * @returns {boolean} True if within limits
 */
export function isWithinLimits(joints, limits) {
  for (let i = 0; i < joints.length; i++) {
    const normalized = normalizeAngle(joints[i]);
    if (normalized < limits[i].min || normalized > limits[i].max) {
      return false;
    }
  }
  return true;
}
